import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './BreathMeditationBasics.css';

const BreathMeditationBasics = () => {
  const { t, i18n } = useTranslation();
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  // Language detection
  const currentLanguage = i18n.language;
  const isSlovak = currentLanguage === 'sk';
  
  // Check if user prefers reduced motion
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  // Breathing techniques data - MOVED HERE BEFORE useEffect
  const breathingTechniques = [
    {
      name: t('breath_meditation.box_breathing.name', 'Box Breathing'),
      description: t('breath_meditation.box_breathing.description', 'A technique used to calm the nervous system by creating a rhythm of 4 counts for each phase of breath.'),
      steps: [
        t('breath_meditation.box_breathing.step1', 'Inhale through your nose for 4 counts'),
        t('breath_meditation.box_breathing.step2', 'Hold your breath for 4 counts'),
        t('breath_meditation.box_breathing.step3', 'Exhale through your mouth for 4 counts'),
        t('breath_meditation.box_breathing.step4', 'Hold your breath for 4 counts'),
        t('breath_meditation.box_breathing.step5', 'Repeat the cycle')
      ]
    },
    {
      name: t('breath_meditation.nadi_shodhana.name', 'Nādi Śodhana'),
      description: t('breath_meditation.nadi_shodhana.description', 'Also known as Alternate-Nostril Breathing, this technique balances the left and right hemispheres of the brain.'),
      steps: [
        t('breath_meditation.nadi_shodhana.step1', 'Close your right nostril with your right thumb and inhale through your left nostril'),
        t('breath_meditation.nadi_shodhana.step2', 'Close your left nostril with your right ring finger, release your thumb, and exhale through your right nostril'),
        t('breath_meditation.nadi_shodhana.step3', 'Inhale through your right nostril'),
        t('breath_meditation.nadi_shodhana.step4', 'Close your right nostril, release your ring finger, and exhale through your left nostril'),
        t('breath_meditation.nadi_shodhana.step5', 'Repeat the cycle')
      ]
    },
    {
      name: t('breath_meditation.kapalbhati.name', 'Kapalbhati'),
      description: t('breath_meditation.kapalbhati.description', 'A cleansing breath technique featuring strong, rapid exhalations and passive inhalations.'),
      steps: [
        t('breath_meditation.kapalbhati.step1', 'Sit comfortably with a straight spine'),
        t('breath_meditation.kapalbhati.step2', 'Take a deep breath in'),
        t('breath_meditation.kapalbhati.step3', 'Exhale forcefully through the nose by contracting your abdominal muscles'),
        t('breath_meditation.kapalbhati.step4', 'Allow your inhalation to happen passively as the abdomen relaxes'),
        t('breath_meditation.kapalbhati.step5', 'Repeat this quick succession of forceful exhalations (aim for 20-30 per round)')
      ]
    }
  ];
  
  // Add structured data for breathing techniques
  useEffect(() => {
    if (breathingTechniques.length > 0) {
      const techniquesData = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": isSlovak ? "Základné dychové techniky" : "Basic Breathing Techniques",
        "itemListElement": breathingTechniques.map((technique, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "HowTo",
            "name": technique.name,
            "description": technique.description,
            "step": technique.steps.map((step, stepIndex) => ({
              "@type": "HowToStep",
              "position": stepIndex + 1,
              "text": step
            }))
          }
        }))
      };
      
      // Add or update JSON-LD script for breathing techniques
      let scriptTag = document.getElementById('breathing-techniques-schema');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.id = 'breathing-techniques-schema';
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(techniquesData);
    }
    
    return () => {
      // Optional cleanup
      const script = document.getElementById('breathing-techniques-schema');
      if (script) {
        script.remove();
      }
    };
  }, [breathingTechniques, isSlovak]);

  return (
    <div 
      className="breath-meditation-container" 
      lang={currentLanguage}
      itemScope 
      itemType="https://schema.org/Article"
    >
      <meta itemProp="name" content={isSlovak ? "Základy dýchania a meditácie" : "Breath & Meditation Basics"} />
      <meta itemProp="headline" content={isSlovak ? "Základy dýchania a meditácie" : "Breath & Meditation Basics"} />
      <meta itemProp="author" content="TiDeaSolis Yoga Štúdio" />
      
      <h3 className="breath-meditation-title" itemProp="headline" lang={currentLanguage}>
        {t('breath_meditation.title', 'Breath & Meditation Basics')}
      </h3>
      
      <div className="breath-meditation-content" itemProp="articleBody">
        <div className="breath-animation-container">
          <div 
            className={`breath-circle ${prefersReducedMotion ? 'paused' : ''}`}
            aria-label={isSlovak ? "Animácia dýchania" : "Breathing animation"}
          >
            <div className="breath-text">
              <span className="breath-phase" lang={currentLanguage}>{t('breath_meditation.breathe', 'Breathe')}</span>
            </div>
          </div>
          <p className="breath-instruction" lang={currentLanguage}>{t('breath_meditation.instruction', '4s inhale • 6s exhale')}</p>
        </div>
        
        <div className="techniques-container" itemScope itemType="https://schema.org/ItemList">
          <meta itemProp="name" content={isSlovak ? "Dychové techniky" : "Breathing Techniques"} />
          
          <div className="technique-tabs" role="tablist" aria-label={isSlovak ? "Dychové techniky" : "Breathing techniques"}>
            {breathingTechniques.map((technique, index) => (
              <button
                key={technique.name}
                className={`technique-tab ${activeTab === index ? 'technique-tab-active' : ''}`}
                onClick={() => setActiveTab(index)}
                role="tab"
                aria-selected={activeTab === index}
                aria-controls={`technique-panel-${index}`}
                id={`technique-tab-${index}`}
                lang={currentLanguage}
              >
                {technique.name}
              </button>
            ))}
          </div>
            
          <div className="technique-panels">
            <div 
              className="technique-panel" 
              role="tabpanel" 
              id={`technique-panel-${activeTab}`}
              aria-labelledby={`technique-tab-${activeTab}`}
              itemScope
              itemType="https://schema.org/HowTo"
            >
              <meta itemProp="name" content={breathingTechniques[activeTab].name} />
              
              <p className="technique-description" itemProp="description" lang={currentLanguage}>
                {breathingTechniques[activeTab].description}
              </p>
              <ol className="technique-steps">
                {breathingTechniques[activeTab].steps.map((step, index) => (
                  <li 
                    key={index} 
                    className="technique-step" 
                    lang={currentLanguage}
                    itemProp="step" 
                    itemScope 
                    itemType="https://schema.org/HowToStep"
                  >
                    <meta itemProp="position" content={index + 1} />
                    <span itemProp="text">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
          
          <div className="audio-container">
            <h4 className="audio-title" lang={currentLanguage}>{t('breath_meditation.audio_title', 'One-Minute Guided Practice')}</h4>
            <audio
              className="meditation-audio"
              controls
              // src="" /* drop MP3 when ready */
              disabled
              aria-label={isSlovak ? "Jednominútová meditácia - audio nahrávka" : "One-minute meditation - audio recording"}
            >
              <p lang={currentLanguage}>{t('breath_meditation.audio_not_supported', 'Your browser does not support the audio element.')}</p>
            </audio>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BreathMeditationBasics; 